/**
 * Turning what is on screen into §6's bounded context.
 *
 * ## The rule this file exists to enforce
 *
 * §6 lists what is sent. Everything else that is in memory — the inventory, the
 * tensor shapes, every computed number, the filter text, the Hub's responses —
 * stays here. That list is not a starting point to be extended when a bug is
 * hard to reproduce: §6 says a new field is "a versioned field added
 * deliberately", and the reason is that this endpoint is the one place the site
 * transmits anything at all, so its payload is the whole of what the privacy
 * claim has to cover.
 *
 * So this function takes a small explicit argument object rather than the app's
 * state. Handing it `AppState` would make every future field one property
 * access away, and the boundary would depend on nobody reaching for it.
 */

import type { ReportContext, ReportFormat, ReportMetric } from "./contract.js";

/** Exactly what the page knows that §6 permits. Nothing takes `AppState`. */
export interface ViewSnapshot {
  readonly format: ReportFormat;
  readonly metric: ReportMetric;
  readonly siteBuild: string;
  /** The canonical component label — what `?part=` carries (§6.0). */
  readonly part?: string;
  /** The repo's own key for that component: a GGUF filename, or `vae/`. */
  readonly partKey?: string;
  readonly tower?: string;
  readonly selectedTensor?: string;
  readonly comparison?: { readonly repoId: string; readonly revisionSha: string };
}

/**
 * An empty string is not a value.
 *
 * The share-card path shipped `part: ""` for every single-model repo, because
 * the root component's prefix literally is the empty string and the boundary
 * below it tested `=== undefined`. `/prepare` answered 400 to every safetensors
 * repo for three hours and the only symptom was cards quietly not existing.
 *
 * `/report` refuses an empty optional too (§8.2.11), so the same shape would
 * fail the same way — with a person watching, this time. Normalised here, at
 * the boundary that knows what these fields mean.
 */
const present = (value: string | undefined): string | undefined =>
  value === undefined || value === "" ? undefined : value;

export function buildContext(snapshot: ViewSnapshot): ReportContext {
  const part = present(snapshot.part);
  return {
    siteBuild: snapshot.siteBuild,
    format: snapshot.format,
    metric: snapshot.metric,
    ...(part !== undefined ? { part } : {}),
    /*
     * `partKey` only travels with `part`. §8.2.11 refuses a raw key without a
     * label — both come from one selection, so one alone means the request was
     * assembled by hand, and `reproductionPath` is built from the label.
     */
    ...(part !== undefined && present(snapshot.partKey) !== undefined
      ? { partKey: snapshot.partKey as string }
      : {}),
    ...(present(snapshot.tower) !== undefined
      ? { tower: snapshot.tower as string }
      : {}),
    ...(present(snapshot.selectedTensor) !== undefined
      ? { selectedTensor: snapshot.selectedTensor as string }
      : {}),
    /*
     * The comparison, only when one actually completed. §6: "exact repo/SHA of
     * B when a completed comparison is active" — a compare pane that is open
     * and still loading has no SHA to pin, and inventing one would put a
     * revision in the record that nobody was looking at.
     */
    ...(snapshot.comparison !== undefined ? { comparison: snapshot.comparison } : {}),
  };
}
